// Canonical API error types shared by both SPAs (R2).
// DetailedError represents a structured error response from the
// Connect-RPC API: { code, message, details, reason }.
// HttpError is intentionally NOT here — it is a legacy app-only type.

export type ErrorCodes =
  | "ok"
  | "canceled"
  | "unknown"
  | "invalid_argument"
  | "deadline_exceeded"
  | "not_found"
  | "already_exists"
  | "permission_denied"
  | "unauthenticated"
  | "resource_exhausted"
  | "failed_precondition"
  | "aborted"
  | "out_of_range"
  | "unimplemented"
  | "internal"
  | "unavailable"
  | "data_loss";

export interface DetailedErrorDetail {
  field: string;
  description: string;
}

// ErrorReason documents the StdError.reason values in use today
// (protos/api/v1/error.proto's own field comment is the source of truth for
// this list). It is widened with `(string & {})` rather than a plain closed
// union: reason is OPTIONAL and a client MUST treat an absent OR
// UNRECOGNIZED value as the generic copy for its `code`, never fail to
// parse — this set grows over time, so the type exists for autocomplete and
// documentation at call sites, never for exhaustiveness at compile time.
export type ErrorReason =
  | "account_membership_not_active"
  | "account_membership_none"
  | "account_already_a_member"
  | "account_invitation_pending"
  | (string & {});

export class DetailedError extends Error {
  statusCode: number;
  code: ErrorCodes;
  details: DetailedErrorDetail[] | undefined;
  // reason is the OPTIONAL machine-readable sub-code (StdError.reason,
  // mirrors google.rpc.ErrorInfo.reason) a caller branches on when several
  // refusals share one `code` — e.g. "account_already_a_member" vs
  // "account_invitation_pending", both `already_exists`. Absent means there
  // is no sub-code for this refusal; callers must treat that as the generic
  // copy for `code`, never fail to handle it.
  reason: ErrorReason | undefined;

  constructor(
    statusCode: number,
    code: ErrorCodes,
    message: string,
    details?: DetailedErrorDetail[],
    reason?: ErrorReason,
  ) {
    super(message);
    this.statusCode = statusCode;
    this.code = code;
    this.details = details;
    this.reason = reason;
  }
}
